import { ChangeDetectionStrategy, Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { NgbOffcanvas, NgbOffcanvasRef } from '@ng-bootstrap/ng-bootstrap';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import { DialogService } from '../dialogs/services/DialogService';
import { LabelService } from '../framework/labels/services/label.service';

@Component({
  selector: 'app-off-canvas',
  templateUrl: './off-canvas.component.html',
  styleUrls: ['./off-canvas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class OffCanvasComponent implements OnInit, OnDestroy {

  @Input() title = '';
  @Input() saveLabel = '';
  @Input() width: number | null = null;
  @Input() openOffcanvas = false;
  @Input() hideFooter = false;
  @Input() hideSaveButton = false;
  @Input() pendingChanges = false;
  @Input() saving = false;
  @Input() formInvalid = false;

  // Templates
  @Input() body!: TemplateRef<any>;
  @Input() header: TemplateRef<any>;
  @Input() footer: TemplateRef<any>;
  @Input() centerFooter: TemplateRef<any>;

  @Output() openOffcanvasChange = new EventEmitter<boolean>();
  @Output() dismiss = new EventEmitter<void>();
  @Output() saveContent = new EventEmitter<boolean>();

  @ViewChild('contentTpl', { static: true }) contentTpl!: TemplateRef<any>;

  private offcanvasRef?: NgbOffcanvasRef;
  private resizing = false;
  private _minWidth = 280;
  private maxWidth = window.innerWidth * 0.9;
  private startX: number;
  private startWidth: number;
  private cachedPanel: HTMLElement | null = null;
  private routerSub: Subscription;
  private bypassPendingChangesConfirmation = false;

  constructor(
    private offcanvasService: NgbOffcanvas,
    private ngZone: NgZone,
    private router: Router,
    private dialogService: DialogService,
    private labelService: LabelService
  ) { }

  ngOnInit() {
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('resize', this.onWindowResize);
    });

    this.routerSub = this.router.events.pipe(
      filter(event => event instanceof NavigationStart)
    ).subscribe((event: NavigationStart) => {
      if (!this.offcanvasRef) {
        return;
      }

      this.ngZone.run(() => {
        this.dismissWithoutPendingChangesConfirmation();
        this.offcanvasRef = undefined;
        this.openOffcanvasChange.emit(false);
      });
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['openOffcanvas']?.currentValue === true) {
      this.ngZone.run(() => this.openAction(this.contentTpl));
    }

    if (changes['openOffcanvas']?.currentValue === false && this.offcanvasRef) {
      this.dismissWithoutPendingChangesConfirmation();
      this.offcanvasRef = undefined;
    }
  }

  public openAction(content: TemplateRef<any>) {
    if (this.offcanvasRef) return;

    this.offcanvasRef = this.offcanvasService.open(this.contentTpl, {
      backdrop: false,
      position: 'end',
      scroll: true,
      panelClass: 'offcanvas-sidebar',
      beforeDismiss: () => this.confirmDismiss()
    });
    const openedOffcanvasRef = this.offcanvasRef;

    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.applyDefaultWidthToOffCanvas();
        this.attachResizeHandlers();
      }, 0);
    });

    const handleClosed = () => {
      this.stopResize();
      if (this.offcanvasRef === openedOffcanvasRef) {
        this.offcanvasRef = undefined;
      }
      this.ngZone.run(() => {
        this.openOffcanvasChange.emit(false);
      });
    };

    // Ngb rejects the result promise when an offcanvas is dismissed. Supplying both
    // handlers avoids creating an unhandled rejected promise on every normal close.
    openedOffcanvasRef.result.then(handleClosed, handleClosed);
  }

  public onSubmit(closeAfterSave: boolean = false) {
    this.saveContent.emit(closeAfterSave);
  }

  public onCancel() {
    this.dismissWithoutPendingChangesConfirmation();
  }

  ngOnDestroy() {
    this.stopResize();
    window.removeEventListener('resize', this.onWindowResize);
    this.routerSub?.unsubscribe();
    this.dismissWithoutPendingChangesConfirmation();
  }

  private applyDefaultWidthToOffCanvas() {
    const panel = this.getPanelElement();
    if (!panel) return;

    const defaultWidth = this.width ?? this._minWidth;

    const width = Math.min(this.maxWidth, Math.max(this._minWidth, defaultWidth));
    panel.style.width = `${width}px`;
  }

  private onWindowResize = () => {
    this.maxWidth = window.innerWidth * 0.9;

    // If it's open, make sure it doesn't exceed maxWidth after resizing viewport
    const panel = this.getPanelElement();
    if (!panel) return;
    const current = parseFloat(panel.style.width || '0');
    if (current > this.maxWidth) {
      panel.style.width = `${this.maxWidth}px`;
    }
  }

  private attachResizeHandlers() {
    if (!this.offcanvasRef) return;

    const panel = this.getPanelElement();
    if (!panel) return;

    const handle = panel.querySelector('.offcanvas-resizer') as HTMLElement;

    if (!handle) {
      console.warn('Missing .offcanvas-resizer');
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      handle.addEventListener('mousedown', this.startResize);
    });
  }

  private startResize = (event: MouseEvent) => {
    event.preventDefault();
    this.resizing = true;

    // Capture baseline at the moment dragging starts
    this.startX = event.clientX;
    this.cachedPanel = this.getPanelElement();
    if (!this.cachedPanel) return;

    this.startX = event.clientX;
    this.startWidth = parseFloat(this.cachedPanel.style.width || '0') || this._minWidth;

    this.cachedPanel.style.transition = 'none';

    document.body.style.cursor = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 640 640'%3E%3Cpath fill='%23333' d='M502.6 438.6L598.6 342.6C611.1 330.1 611.1 309.8 598.6 297.3L502.6 201.3C490.1 188.8 469.8 188.8 457.3 201.3C444.8 213.8 444.8 234.1 457.3 246.6L498.7 288L141.2 288L182.6 246.6C195.1 234.1 195.1 213.8 182.6 201.3C170.1 188.8 149.8 188.8 137.3 201.3L41.3 297.3C35.3 303.3 31.9 311.4 31.9 319.9C31.9 328.4 35.3 336.5 41.3 342.5L137.3 438.5C149.8 451 170.1 451 182.6 438.5C195.1 426 195.1 405.7 182.6 393.2L141.2 351.8L498.7 351.8L457.3 393.2C444.8 405.7 444.8 426 457.3 438.5C469.8 451 490.1 451 502.6 438.5z'/%3E%3C/svg%3E") 12 12, col-resize`;

    document.body.style.userSelect = 'none';

    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.stopResize);
  };

  private onMouseMove = (event: MouseEvent) => {
    if (!this.resizing || !this.cachedPanel) return;

    const delta = this.startX - event.clientX;
    const width = Math.min(this.maxWidth, Math.max(this._minWidth, this.startWidth + delta));

    this.cachedPanel.style.width = `${width}px`;
  };

  private stopResize = () => {
    if (!this.resizing) return;
    this.resizing = false;

    if (this.cachedPanel) {
      this.cachedPanel.style.transition = 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)';
    }

    this.cachedPanel = null;

    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.stopResize);
  };

  private async confirmDismiss(): Promise<boolean> {
    if (this.bypassPendingChangesConfirmation || !this.pendingChanges) {
      return true;
    }

    const labelContext = await firstValueFrom(this.labelService.getContext('jobs'));
    const message = labelContext.get('closing_without_saving_message').labelText;
    const title = labelContext.get('closing_without_saving_title').labelText;

    return this.dialogService
      .confirm({ message, title, centered: false })
      .then(() => true, () => false);
  }

  private dismissWithoutPendingChangesConfirmation(): void {
    this.stopResize();
    this.bypassPendingChangesConfirmation = true;
    this.offcanvasRef?.dismiss();
    this.bypassPendingChangesConfirmation = false;
  }

  private getPanelElement(): HTMLElement | null {
    if (!this.offcanvasRef) return null;

    return (this.offcanvasRef as any)
      ?._panelCmptRef
      ?.location
      ?.nativeElement ?? null;
  }
}
